// Pure NDJSON line splitter. Feed it chunks (strings, the way TextDecoder
// would emit), get back complete lines as they cross newline boundaries.
// Trailing partial line stays buffered until the next chunk or flush().

export class NdjsonReader {
  private buffer = '';

  // Push a chunk of decoded text. Returns the complete lines (without trailing
  // \n) that were closed by this chunk. Empty/whitespace-only lines are dropped.
  push(chunk: string): string[] {
    if (chunk.length === 0) return [];
    this.buffer += chunk;
    const out: string[] = [];
    let nl = this.buffer.indexOf('\n');
    while (nl !== -1) {
      const line = this.buffer.slice(0, nl);
      this.buffer = this.buffer.slice(nl + 1);
      const trimmed = line.trim();
      if (trimmed.length > 0) out.push(trimmed);
      nl = this.buffer.indexOf('\n');
    }
    return out;
  }

  // Final call — return any trailing line that didn't end with \n.
  flush(): string[] {
    const trimmed = this.buffer.trim();
    this.buffer = '';
    return trimmed.length > 0 ? [trimmed] : [];
  }
}

// Drives a fetch ReadableStream → NdjsonReader → JSON-parsed events.
export async function readNdjsonStream<T>(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: T) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const split = new NdjsonReader();

  const onAbort = () => {
    void reader.cancel().catch(() => {});
  };
  signal?.addEventListener('abort', onAbort, { once: true });

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (value && value.byteLength > 0) {
        for (const line of split.push(decoder.decode(value, { stream: true }))) {
          onEvent(JSON.parse(line) as T);
        }
      }
      if (done) {
        for (const line of split.flush()) {
          onEvent(JSON.parse(line) as T);
        }
        return;
      }
    }
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
}
